import { getAllEmailLogs, deleteEmailLogs, getAllIpEnrichments } from '../services/storage.js';
import { whoisLookup } from '../services/whois.js';
import { updateGeoFromWhois, updateGeoFromShodan } from '../services/geoip.js';
import { checkShodan } from '../services/threat-intel.js';
import { html, concurrencyPool, truncate } from '../services/util.js';
import { ConfirmDialog } from './confirm-dialog.js';
import './toast-container.js';

const PAGE_SIZES = [50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;

const template = document.createElement('template');
template.innerHTML = `
  <div class="section-header">
    <h1>Email Log Review</h1>
    <div class="section-actions">
      <input type="text" id="email-search" placeholder="Search sender, IP, or subject..." style="width: 220px;">
      <select id="email-source-filter">
        <option value="">All</option>
        <option value="Exchange">Exchange</option>
        <option value="Gmail">Gmail</option>
      </select>
      <select id="email-status-filter">
        <option value="">All Status</option>
        <option value="Success">Success</option>
        <option value="Failure">Failure</option>
      </select>
    </div>
  </div>

  <div id="email-mass-action-bar" class="mass-action-bar" style="display:none">
    <span class="mass-count"><span id="email-selected-count">0</span> selected</span>
    <span style="color: var(--text-muted); font-size: 0.8rem;">Actions:</span>
    <button class="btn btn-sm btn-secondary" id="email-enrich-geo-whois">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
      Enrich Geo WHOIS
    </button>
    <button class="btn btn-sm btn-secondary" id="email-enrich-geo-shodan">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M21 12a9 9 0 01-9 9"/><path d="M3 12a9 9 0 019-9"/><circle cx="12" cy="12" r="3"/></svg>
      Enrich Geo Shodan
    </button>
    <button class="btn btn-sm btn-secondary" id="email-delete-selected">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
      Delete Selected
    </button>
    <button class="btn btn-sm btn-secondary" id="email-clear-selection">Clear</button>
  </div>

  <div class="pagination-bar">
    <span id="email-count"></span>
    <span style="flex:1"></span>
    <span style="display:flex;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-muted)">
      Rows:
      <select id="email-page-size" style="font-size:0.75rem;padding:2px 6px;border-radius:4px;border:1px solid var(--border-color);background:var(--bg-input);color:var(--text-primary)">
        ${PAGE_SIZES.map(s => `<option value="${s}" ${s === DEFAULT_PAGE_SIZE ? 'selected' : ''}>${s}</option>`).join('')}
      </select>
    </span>
    <button class="btn btn-sm btn-secondary" id="email-page-prev" disabled>Prev</button>
    <span id="email-page-info"></span>
    <button class="btn btn-sm btn-secondary" id="email-page-next">Next</button>
  </div>
  <div class="table-wrapper">
    <table class="data-table" id="email-log-table">
      <thead>
        <tr>
          <th style="width:36px"><input type="checkbox" id="email-select-all"></th>
          <th data-sort="timestamp" class="sortable">Timestamp <span class="sort-arrow"></span></th>
          <th data-sort="sender" class="sortable">Sender <span class="sort-arrow"></span></th>
          <th data-sort="recipients" class="sortable">Recipients <span class="sort-arrow"></span></th>
          <th data-sort="subject" class="sortable">Subject <span class="sort-arrow"></span></th>
          <th data-sort="senderIp" class="sortable">Sender IP <span class="sort-arrow"></span></th>
          <th data-sort="country" class="sortable">Country <span class="sort-arrow"></span></th>
          <th data-sort="status" class="sortable">Status <span class="sort-arrow"></span></th>
          <th data-sort="source" class="sortable">Source <span class="sort-arrow"></span></th>
        </tr>
      </thead>
      <tbody id="email-log-tbody"></tbody>
    </table>
  </div>
  <div id="email-log-empty" class="empty-state" style="display:none">
    <div class="empty-icon">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.4">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      </svg>
    </div>
    <div class="empty-text">No email logs found</div>
    <div class="empty-hint">Import email logs first</div>
  </div>
`;

export class EmailLogTable extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = template.innerHTML;
    this._allLogs = [];
    this._enrichments = [];
    this._enrichByIP = {};
    this._debounceTimer = null;
    this._page = 0;
    this._pageSize = DEFAULT_PAGE_SIZE;
    this._sortField = 'timestamp';
    this._sortDir = -1;
    this._refreshGen = 0;
  }

  disconnectedCallback() {
    clearTimeout(this._debounceTimer);
  }

  connectedCallback() {
    this.querySelector('#email-search').addEventListener('input', () => this.debounceRefresh());
    this.querySelector('#email-source-filter').addEventListener('change', () => this.refresh());
    this.querySelector('#email-status-filter').addEventListener('change', () => this.refresh());
    this.querySelector('#email-page-size').addEventListener('change', (e) => {
      this._pageSize = Number(e.target.value);
      this._page = 0;
      this.refresh();
    });

    this.querySelector('#email-select-all').addEventListener('change', (e) => {
      const checked = e.target.checked;
      this.querySelectorAll('.email-log-checkbox').forEach(cb => cb.checked = checked);
      this.updateMassActionBar();
    });

    this.querySelector('#email-page-prev').addEventListener('click', () => {
      if (this._page > 0) { this._page--; this.render(); }
    });
    this.querySelector('#email-page-next').addEventListener('click', () => {
      const totalPages = Math.ceil(this._allLogs.length / this._pageSize);
      if (this._page < totalPages - 1) { this._page++; this.render(); }
    });

    this.querySelector('#email-enrich-geo-whois').addEventListener('click', () => this.enrichSelectedGeo());
    this.querySelector('#email-enrich-geo-shodan').addEventListener('click', () => this.enrichSelectedGeoFromShodan());
    this.querySelector('#email-delete-selected').addEventListener('click', () => this.deleteSelected());
    this.querySelector('#email-clear-selection').addEventListener('click', () => this.clearSelection());

    this.querySelectorAll('.sortable').forEach(th => {
      th.addEventListener('click', () => this.sortBy(th.dataset.sort));
    });

    document.addEventListener('threatscope:stats-update', () => this.refresh());
    document.addEventListener('threatscope:data-cleared', () => { this._allLogs = []; this._page = 0; this.render(); });
    this.refresh();
  }

  sortBy(field) {
    if (this._sortField === field) {
      this._sortDir *= -1;
    } else {
      this._sortField = field;
      this._sortDir = 1;
    }
    this.render();
  }

  debounceRefresh() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.refresh(), 250);
  }

  async refresh() {
    const gen = ++this._refreshGen;
    const search = (this.querySelector('#email-search').value || '').toLowerCase();
    const sourceFilter = this.querySelector('#email-source-filter').value;
    const statusFilter = this.querySelector('#email-status-filter').value;

    const filters = {};
    if (sourceFilter) filters.source = sourceFilter;
    if (statusFilter) filters.status = statusFilter;

    const allLogs = await getAllEmailLogs(filters);
    if (gen !== this._refreshGen) return;

    const enrichments = await getAllIpEnrichments();
    if (gen !== this._refreshGen) return;

    const enrichByIP = {};
    enrichments.forEach(e => { enrichByIP[e.ip] = e; });

    let filtered = allLogs;

    if (search) {
      filtered = filtered.filter(l => {
        const enrich = enrichByIP[l.senderIp];
        return (l.sender || '').toLowerCase().includes(search) ||
          (l.senderIp || '').toLowerCase().includes(search) ||
          (l.subject || '').toLowerCase().includes(search) ||
          (l.recipients || '').toLowerCase().includes(search) ||
          (enrich && enrich.country && enrich.country.toLowerCase().includes(search));
      });
    }

    if (gen !== this._refreshGen) return;

    this._allLogs = filtered;
    this._enrichments = enrichments;
    this._enrichByIP = enrichByIP;
    this._page = 0;
    this.render();
  }

  render() {
    const tbody = this.querySelector('#email-log-tbody');
    const empty = this.querySelector('#email-log-empty');
    const enrichByIP = this._enrichByIP;

    const sorted = [...this._allLogs].sort((a, b) => {
      let aVal, bVal;
      if (this._sortField === 'country') {
        const ea = enrichByIP[a.senderIp];
        const eb = enrichByIP[b.senderIp];
        aVal = (ea && ea.country) || '';
        bVal = (eb && eb.country) || '';
      } else if (this._sortField === 'timestamp') {
        aVal = a.timestamp || '';
        bVal = b.timestamp || '';
      } else {
        aVal = (a[this._sortField] || '').toLowerCase();
        bVal = (b[this._sortField] || '').toLowerCase();
      }
      if (aVal < bVal) return -1 * this._sortDir;
      if (aVal > bVal) return 1 * this._sortDir;
      return 0;
    });

    if (sorted.length === 0) {
      tbody.innerHTML = '';
      empty.style.display = '';
      this.querySelector('.pagination-bar').style.display = 'none';
      this.querySelector('#email-mass-action-bar').style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    this.querySelector('.pagination-bar').style.display = 'flex';

    const totalPages = Math.ceil(sorted.length / this._pageSize);
    const start = this._page * this._pageSize;
    const pageLogs = sorted.slice(start, start + this._pageSize);

    this.querySelector('#email-count').textContent = `${sorted.length} email logs`;
    this.querySelector('#email-page-info').textContent = `Page ${this._page + 1} of ${totalPages || 1}`;
    this.querySelector('#email-page-prev').disabled = this._page === 0;
    this.querySelector('#email-page-next').disabled = this._page >= totalPages - 1;

    this.querySelectorAll('.sortable').forEach(th => {
      const arrow = th.querySelector('.sort-arrow');
      if (th.dataset.sort === this._sortField) {
        arrow.textContent = this._sortDir === 1 ? ' \u25B2' : ' \u25BC';
        arrow.style.opacity = '1';
      } else {
        arrow.textContent = '';
      }
    });

    tbody.innerHTML = pageLogs.map(log => {
      const enrich = enrichByIP[log.senderIp];
      const country = enrich ? enrich.country : '';

      return `<tr class="clickable-row" data-id="${log.id}">
        <td class="checkbox-cell"><input type="checkbox" class="email-log-checkbox" data-id="${log.id}"></td>
        <td>${log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
        <td title="${html(log.sender)}">${html(truncate(log.sender, 24))}</td>
        <td title="${html(log.recipients)}">${html(truncate(log.recipients, 24))}</td>
        <td title="${html(log.subject)}">${html(truncate(log.subject, 30))}</td>
        <td><code>${html(log.senderIp)}</code></td>
        <td>${country ? `<span class="country-cell">${country}</span>` : '-'}</td>
        <td><span class="badge ${log.status === 'Success' ? 'badge-status-resolved' : log.status === 'Failure' ? 'badge-status-active' : ''}">${html(log.status || '-')}</span></td>
        <td><span class="badge badge-source-${log.source}">${html(log.source)}</span></td>
      </tr>`;
    }).join('');

    tbody.querySelectorAll('.clickable-row').forEach(row => {
      row.addEventListener('click', (e) => {
        if (e.target.type === 'checkbox') return;
        const id = Number(row.dataset.id);
        const log = sorted.find(l => l.id === id);
        if (log) {
          const enrich = enrichByIP[log.senderIp];
          document.dispatchEvent(new CustomEvent('threatscope:show-detail', {
            detail: { type: 'emaillog', emailLog: log, enrichment: enrich },
          }));
        }
      });
    });

    tbody.querySelectorAll('.email-log-checkbox').forEach(cb => {
      cb.addEventListener('change', () => this.updateMassActionBar());
    });
  }

  updateMassActionBar() {
    const selected = this.querySelectorAll('.email-log-checkbox:checked');
    const bar = this.querySelector('#email-mass-action-bar');
    const count = this.querySelector('#email-selected-count');

    if (selected.length > 0) {
      bar.style.display = 'flex';
      count.textContent = selected.length;
    } else {
      bar.style.display = 'none';
    }
  }

  clearSelection() {
    this.querySelectorAll('.email-log-checkbox').forEach(cb => cb.checked = false);
    this.querySelector('#email-select-all').checked = false;
    this.updateMassActionBar();
  }

  async deleteSelected() {
    const ids = this.getSelectedIds();
    if (ids.length === 0) return;

    const confirmed = await ConfirmDialog.show({
      title: 'Delete Email Log Entries',
      message: `Delete ${ids.length} selected email log entr${ids.length === 1 ? 'y' : 'ies'}? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    await deleteEmailLogs(ids);
    this.clearSelection();
    this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
    window.showToast(`${ids.length} email log entr${ids.length === 1 ? 'y' : 'ies'} deleted`, 'info');
  }

  async enrichSelectedGeo() {
    const ids = this.getSelectedIds();
    if (ids.length === 0) return;

    const logs = this._allLogs.filter(l => ids.includes(l.id));
    const enrichByIP = this._enrichByIP;
    const ips = [...new Set(logs.map(l => l.senderIp).filter(ip => {
      const enrich = enrichByIP[ip];
      return !enrich || !enrich.country;
    }))];

    if (ips.length === 0) {
      window.showToast('All selected IPs already have geo data', 'info');
      this.clearSelection();
      return;
    }

    const confirmed = await ConfirmDialog.show({
      title: 'Enrich Geo from WHOIS',
      message: `Look up WHOIS records for ${ips.length} IP${ips.length === 1 ? '' : 's'} to populate geolocation data?`,
      confirmText: 'Enrich',
    });
    if (!confirmed) return;

    window.showToast(`Enriching ${ips.length} IP${ips.length === 1 ? '' : 's'} from WHOIS...`, 'info', 0);

    const { failures } = await concurrencyPool(ips, async (ip) => {
      const whoisData = await whoisLookup(ip);
      if (whoisData) {
        await updateGeoFromWhois(ip, whoisData);
      }
    }, 5);

    const msg = failures > 0
      ? `Geo data enriched for ${ips.length} IPs (${failures} failures)`
      : `Geo data enriched for ${ips.length} IP${ips.length === 1 ? '' : 's'}`;
    window.showToast(msg, failures > 0 && failures === ips.length ? 'error' : 'success');
    this.clearSelection();
    await this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
  }

  async enrichSelectedGeoFromShodan() {
    const ids = this.getSelectedIds();
    if (ids.length === 0) return;

    const logs = this._allLogs.filter(l => ids.includes(l.id));
    const enrichByIP = this._enrichByIP;
    let ips = [...new Set(logs.map(l => l.senderIp).filter(ip => {
      const enrich = enrichByIP[ip];
      return !enrich || !enrich.country;
    }))];

    if (ips.length === 0) {
      window.showToast('All selected IPs already have geo data', 'info');
      this.clearSelection();
      return;
    }

    const confirmed = await ConfirmDialog.show({
      title: 'Enrich Geo from Shodan',
      message: `Look up Shodan data for ${ips.length} IP${ips.length === 1 ? '' : 's'} to populate geolocation?`,
      confirmText: 'Enrich',
    });
    if (!confirmed) return;

    window.showToast(`Enriching ${ips.length} IP${ips.length === 1 ? '' : 's'} from Shodan...`, 'info', 0);

    let enriched = 0;
    const { failures } = await concurrencyPool(ips, async (ip) => {
      const enrich = enrichByIP[ip];
      let shodanData = enrich?.threatIntel?.shodan;

      if (!shodanData?.countryName) {
        shodanData = await checkShodan(ip);
      }

      if (shodanData?.countryName) {
        const result = await updateGeoFromShodan(ip, shodanData);
        if (result) enriched++;
      }
    }, 2);

    const msg = `Geo data enriched for ${enriched} IP${enriched === 1 ? '' : 's'} from Shodan`;
    window.showToast(
      failures > 0 && failures === ips.length ? `${msg} (all failed)` : msg,
      enriched > 0 ? 'success' : 'info'
    );
    this.clearSelection();
    await this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
  }

  getSelectedIds() {
    return [...this.querySelectorAll('.email-log-checkbox:checked')].map(cb => Number(cb.dataset.id));
  }

}

customElements.define('email-log-table', EmailLogTable);
